/**
 * Tiny Result type for expected failures (parse, normalize, decode).
 *
 * Prefer this over throw/`null` in domain and decode code. Reserve throw for
 * programmer bugs and island/native boundaries. See docs/fp-conventions.md.
 */

export type Ok<T> = { readonly ok: true; readonly value: T };
export type Err<E> = { readonly ok: false; readonly error: E };
export type Result<T, E = string> = Ok<T> | Err<E>;

export function ok<T>(value: T): Ok<T> {
  return { ok: true, value };
}

export function err<E>(error: E): Err<E> {
  return { ok: false, error };
}

export function isOk<T, E>(result: Result<T, E>): result is Ok<T> {
  return result.ok;
}

export function isErr<T, E>(result: Result<T, E>): result is Err<E> {
  return result.ok === false;
}

/** Map the Ok value; leave Err unchanged. */
export function mapOk<T, U, E>(
  result: Result<T, E>,
  fn: (value: T) => U,
): Result<U, E> {
  return result.ok ? ok(fn(result.value)) : result;
}

/** Chain: Ok runs `fn`; Err short-circuits. */
export function andThen<T, U, E>(
  result: Result<T, E>,
  fn: (value: T) => Result<U, E>,
): Result<U, E> {
  return result.ok ? fn(result.value) : result;
}

/** Unwrap Ok or replace Err with a fallback value. */
export function unwrapOr<T, E>(result: Result<T, E>, fallback: T): T {
  return result.ok ? result.value : fallback;
}

/** Collect Ok values; drop Errs (useful when skipping bad list items). */
export function valuesOk<T, E>(results: ReadonlyArray<Result<T, E>>): T[] {
  const out: T[] = [];
  for (const result of results) {
    if (result.ok) out.push(result.value);
  }
  return out;
}
